import {injectIntl} from "react-intl";
import {AutoRetrieverDeviceOverviewContext} from "./context/AutoRetrieverDeviceOverviewContext";
import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import {
    assetTypeToConfigTypeDict,
    getAssetRefreshInterval, getCurrentDate,
    getStatusRefreshInterval,
    sortComparator
} from "../../util/Util";
import {ACTIVE, ACTIONS_STRING, ASCENDING, BLUE_COLOR, DEFAULT_VENDOR_ATTRIBUTES, ORANGE_COLOR} from "../../util/Constants";
import {getLocalizedMessage} from "../../util/Localization";
import Loader from "@amzn/meridian/loader";
import Row from "@amzn/meridian/row";
import RefreshTimer from "../../util/RefreshTimer";
import Box from "@amzn/meridian/box";
import Tooltip from "@amzn/meridian/tooltip";
import Column from "@amzn/meridian/column";
import Button from "@amzn/meridian/button";
import Text from "@amzn/meridian/text";
import Icon from "@amzn/meridian/icon";
import chevronDownLargeTokens from "@amzn/meridian-tokens/base/icon/chevron-down-large";
import Alert from "@amzn/meridian/alert";
import Table, {TableCell, TableRow} from "@amzn/meridian/table";
import DeleteSingleDeviceModal from "../../commonui/DeleteSingleDeviceModal";
import trashTokens from "@amzn/meridian-tokens/base/icon/trash";
import EditMacAddressModal from "../../commonui/EditMacAddressModal";
import plusCircleTokens from "@amzn/meridian-tokens/base/icon/plus-circle";
import DisableMonitoringModal from "../../commonui/DisableMonitoringModal";
import {
    checkDeviceMonitoringEnabled,
    getMonitoringStatusDetails,
    SPEC_VERSION_DISPLAY_VALUES,
    VENDOR_DISPLAY_VALUES
} from "../../util/Validations";
import Menu, {MenuItem} from "@amzn/meridian/menu";
import AddAutoRetrieverSingleDeviceModal from "./modal/AddAutoRetrieverSingleDeviceModal";

const AutoRetrieverDeviceOverview = ({intl}) => {

    const {viewModel, dispatch} = useContext(AutoRetrieverDeviceOverviewContext);
    const [selected, setSelected] = useState("");
    const actionButtonRefs = useRef([]);
    const tableEndRef = useRef(null)
    const [openActionsMenu, setOpenActionsMenu] = useState({});
    const [mac, setMac] = useState("");

    // Handle modal state (open/hidden)
    const [openAddModal, setOpenAddModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false)
    const [openEditMacModal, setOpenEditMacModal] = useState(false)
    const [openDisableMonitoringModal, setOpenDisableMonitoringModal] = useState(false)

    const onClickActionsButton = useCallback((index) => {
        let _open = {...openActionsMenu};
        _open[index] = true;
        setOpenActionsMenu(_open);
    }, []);
    const onCloseActions = useCallback((index) => {
        let _open = {...openActionsMenu};
        _open[index] = false;
        setOpenActionsMenu(_open)
    }, []);

    useEffect(() => {
        if (!openAddModal) { // fetch overview data in regular intervals if the add device modal is not open
            getData()
            const assetInterval = setInterval(() => {
                if (!document.hidden) {
                    getData(); // get asset data
                }
            }, getAssetRefreshInterval());
            const statusInterval = setInterval(() => {
                if (!document.hidden) {
                    getStatusData(); // get status update
                }
            }, getStatusRefreshInterval());

            return () => {
                clearInterval(assetInterval);
                clearInterval(statusInterval);
            }
        }
    }, [openAddModal]);

    useEffect(() => {
        actionButtonRefs.current = actionButtonRefs.current.slice(0, viewModel.autoRetrieverDeviceInformation.length);
    }, [viewModel.autoRetrieverDeviceInformation])

    const getData = () => {
        setOpenAddModal(false)
        setOpenDeleteModal(false)
        setOpenEditMacModal(false)
        setOpenDisableMonitoringModal(false)
        dispatch({type: 'LOAD_VIEW', data: {dispatcher: dispatch}});
    }
    const getStatusData = () => {
        dispatch({type: 'LOAD_STATUS', data: {dispatcher: dispatch, state: viewModel}});
    }

    const scrollToBottom = () => {
        tableEndRef.current.scrollIntoView({behavior: "smooth"})
    }

    const onSort = (sortDetails) => {
        dispatch({
            type: 'SORT_DATA',
            data: {column: sortDetails.sortColumn, direction: sortDetails.sortDirection}
        })
    }

    const onClickAddEditConfig = () => {
        setSelected("");
        setOpenAddModal(true);
    }

    const onClickEditDevice = (mac) => {
        setSelected(mac)
        setOpenAddModal(true)
    }

    const onClickRemove = (mac) => {
        setSelected(mac)
        setOpenDeleteModal(true)
    }

    const onClickChangeMac = (mac) => {
        setSelected(mac);
        setOpenEditMacModal(true);
    }

    const onClickDisableMonitoring = (mac) => {
        setMac(mac)
        setOpenDisableMonitoringModal(true)
    }
    const onClickEnableMonitoring = (startTime, reason, mac) => {
        const stateValues = {
            "startTime": startTime,
            "endTime": getCurrentDate().toString(),
            "reason": reason
        }
        updateData(stateValues, mac);
        getData();
    }

    const updateData = (stateValues, mac) => {
        dispatch({type: 'UPDATE_MONITORING_DATA', data: {dispatcher: dispatch, stateValues: stateValues, mac: mac}})
    }

    const getAutoRetrieverStateValues = (mac) => {
        if(!mac) return {};
        const device = viewModel.autoRetrieverDeviceInformation[mac];
        if(!device) return {};
        const deviceMonitoring = {
            startTime: device.startTime,
            endTime: device.endTime,
            reason: device.reason
        }
        return {
            name: device.name,
            mac: mac,
            ip: device.ip,
            port: device.port,
            deviceType: device.deviceType,
            deviceConfig: JSON.stringify({
                type: assetTypeToConfigTypeDict.typeAUTO_RETRIEVER,
                id: device.id
            }),
            vendorAttributes: device.vendorAttributes,
            monitoring: JSON.stringify(deviceMonitoring)
        }
    }

    const renderDeviceMonitoring = (monitoringStatusColor, monitoringStatusId) => {
        return (<b style={{color: monitoringStatusColor}}>
            {getLocalizedMessage({intl, stringId: monitoringStatusId})}
        </b>)
    }

    const sortedDeviceList = Object.values(viewModel.autoRetrieverDeviceInformation);
    sortedDeviceList && sortedDeviceList.sort((a, b) => {
        const {column, direction} = viewModel.sortPref;
        let compareValue;
        if (Object.keys(DEFAULT_VENDOR_ATTRIBUTES).includes(column)) {
            if(column === "vendor"){
                compareValue = sortComparator(a.vendorAttributes[column] + a.vendorAttributes["spec_version"],
                    b.vendorAttributes[column] + b.vendorAttributes["spec_version"]);
            } else {
                compareValue = sortComparator(a.vendorAttributes[column], b.vendorAttributes[column]);
            }
        } else {
            compareValue = sortComparator(a[column], b[column])
        }
        if (direction === ASCENDING) {
            return compareValue;
        } else {
            return -compareValue;
        }
    })

    if (viewModel.loadingOverview) {
        return (<Loader/>)
    } else {
        return (
            <Row widths="100%" alignmentVertical="top" spacingInset="none none large none">
                <Column>
                    <Row width="100%" widths={["100", "fill", "100"]}>
                        <RefreshTimer deps={[viewModel]}/>
                        <Box></Box>
                        <Tooltip position="top" title={getLocalizedMessage({intl, stringId: "tooltip.scrolltobottom"})}>
                            <Button onClick={scrollToBottom}
                                    type="secondary"
                                    size="small">
                                <Icon tokens={chevronDownLargeTokens}/>
                            </Button>
                        </Tooltip>
                    </Row>
                    {sortedDeviceList.length === 0 ?
                        <Alert type="informational" size="small">
                            {getLocalizedMessage({intl, stringId: "alert.device"})}
                        </Alert> :
                        <Table
                            headerRows={1}
                            spacing="small"
                            sortColumn={viewModel.sortPref.column}
                            sortDirection={viewModel.sortPref.direction}
                            onSort={onSort}
                            showDividers={true}
                            showStripes={true}>
                            <TableRow highlightOnHover={false}>
                                <TableCell sortColumn="name">
                                    {getLocalizedMessage({intl, stringId: "device.name"})}
                                </TableCell>
                                <TableCell sortColumn="vendor">
                                    {getLocalizedMessage({intl, stringId: 'device.vendor'})}
                                </TableCell>
                                <TableCell sortColumn="mac">
                                    {getLocalizedMessage({intl, stringId: "device.mac_ip"})}
                                </TableCell>
                                <Tooltip position={"top"} title={getLocalizedMessage({intl, stringId: 'tooltip.connectionstatus'})}>
                                    <TableCell sortColumn="connection">
                                        <b>
                                            {getLocalizedMessage({intl, stringId: "device.connection"})}
                                        </b>
                                    </TableCell>
                                </Tooltip>
                                <TableCell>
                                    {getLocalizedMessage({intl, stringId: "device.monitoring"})}
                                </TableCell>
                                <TableCell>
                                    {getLocalizedMessage({intl, stringId: "device.action"})}
                                </TableCell>
                            </TableRow>
                            {
                                sortedDeviceList.map((device, index) => {
                                    const vendorAttributes = device.vendorAttributes;
                                    const monitoring = checkDeviceMonitoringEnabled(Number(device.startTime), Number(device.endTime));
                                    const {monitoringStatusColor, monitoringStatusId} = getMonitoringStatusDetails(monitoring);
                                    return (
                                        <TableRow key={index} highlightOnHover={true}>
                                            <TableCell>
                                                <b>{device.name}</b>
                                                <br></br>
                                                {device.deviceType}
                                            </TableCell>
                                            <TableCell>
                                                <Column>
                                                    <b>{VENDOR_DISPLAY_VALUES[vendorAttributes.vendor]}</b>
                                                    {SPEC_VERSION_DISPLAY_VALUES[vendorAttributes.spec_version]}
                                                </Column>
                                            </TableCell>
                                            <TableCell>
                                                <Column>
                                                    <b>{device.mac}</b>
                                                    {device.ip}:{device.port}
                                                </Column>
                                            </TableCell>
                                            <TableCell>
                                                {device.assetStatus ?
                                                    <p style={{color: device.assetStatus.toUpperCase() === ACTIVE ? BLUE_COLOR : ORANGE_COLOR}}>
                                                        <b>{device.assetStatus}</b>
                                                    </p> :
                                                    getLocalizedMessage({intl, stringId: "loading.text"})
                                                }
                                            </TableCell>
                                            <TableCell>
                                                {renderDeviceMonitoring(monitoringStatusColor, monitoringStatusId)}
                                            </TableCell>
                                            <TableCell>
                                                <Text type="b300">
                                                    <Button ref={el => actionButtonRefs.current[index] = el}
                                                            type="icon"
                                                            size="medium"
                                                            onClick={() => onClickActionsButton(index)}
                                                            aria-controls="my-menu"
                                                            aria-haspopup="menu"
                                                            aria-expanded="false">
                                                        {ACTIONS_STRING}
                                                    </Button>
                                                    <Menu anchorNode={actionButtonRefs.current[index]}
                                                          open={openActionsMenu[index] ? openActionsMenu[index] : false}
                                                          position="left"
                                                          onClose={() => onCloseActions(index)}>
                                                        <MenuItem onClick={() => onClickEditDevice(device.mac)}>
                                                            {getLocalizedMessage({intl, stringId: "actions.addDevice"})}
                                                        </MenuItem>
                                                        <MenuItem onClick={() => onClickRemove(device.mac)}>
                                                            {getLocalizedMessage({intl, stringId: "actions.deleteDevice"})}
                                                        </MenuItem>
                                                        <MenuItem onClick={() => onClickChangeMac(device.mac)}>
                                                            {getLocalizedMessage({intl, stringId: "actions.changeMac"})}
                                                        </MenuItem>
                                                        {(monitoring.isMonitoringEnabled) ?
                                                            <MenuItem onClick={() => onClickDisableMonitoring(device.mac)}>
                                                                {getLocalizedMessage({intl, stringId: "actions.monitoring_disable"})}
                                                            </MenuItem> : <MenuItem
                                                                onClick={() => onClickEnableMonitoring(device.startTime, device.reason, device.mac)}>
                                                                {getLocalizedMessage({intl, stringId: "actions.monitoring_enable"})}
                                                            </MenuItem>}
                                                    </Menu>
                                                </Text>
                                            </TableCell>
                                        </TableRow>
                                    )
                                })
                            }
                        </Table>
                    }
                    <Row width="100%" widths={["100", "fill", "100"]}>
                        <Button onClick={onClickAddEditConfig} type="primary" size="small">
                            <Icon tokens={plusCircleTokens}/>
                            {getLocalizedMessage({intl, stringId: "device.add_device_button_modal"})}
                        </Button>
                        <Box></Box>
                        <Button onClick={() => onClickRemove("")} type="tertiary" size="small">
                            <Icon tokens={trashTokens}/>
                            {getLocalizedMessage({intl, stringId: "button.delete"})}
                        </Button>
                    </Row>
                    <AddAutoRetrieverSingleDeviceModal open={openAddModal} setOpen={setOpenAddModal}
                                                   selected={selected} setSelected={setSelected}/>
                    <DeleteSingleDeviceModal open={openDeleteModal} setOpen={setOpenDeleteModal}
                                             selected={selected} setSelected={setSelected}
                                             macAddressArray={viewModel.macAddressArray} dispatch={dispatch}/>
                    <EditMacAddressModal open={openEditMacModal} setOpen={setOpenEditMacModal}
                                         selectedMac={selected} setSelectedMac={setSelected} dispatch={dispatch}
                                         stateValues={getAutoRetrieverStateValues(selected)}/>
                    {openDisableMonitoringModal && <DisableMonitoringModal mac={mac} state={openDisableMonitoringModal}
                                                                           onStateChange={setOpenDisableMonitoringModal}
                                                                           context={AutoRetrieverDeviceOverviewContext}
                                                                           reducer={'UPDATE_MONITORING_DATA'}/>}
                    <div ref={tableEndRef}/>
                </Column>
            </Row>
        )
    }
}

export default injectIntl(AutoRetrieverDeviceOverview)


// WEBPACK FOOTER //
// ./src/components/devicemanagement/autoretriever/AutoRetrieverDeviceOverview.js